import { Outlet, NavLink } from 'react-router-dom';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Container from 'react-bootstrap/Container';
import LinkContainer from 'react-router-bootstrap/LinkContainer';
import { ToastContainer, toast } from 'react-toastify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Api,
  environment,
  fetchUserBusinessUnit,
  fetchUserLastDay,
} from './components/interface';
import Logo from './elm3.png';
// import { IsRole } from "./components/validator";
import { Hint, LINKS } from './components/constants';

import {
  handleResize,
  checkVersion,
  setEnvironmentVariables,
  bake,
} from './components/common';
import { BellFill, Magic, HouseFill, Globe } from 'react-bootstrap-icons';
import { NestedMenuItem } from 'mui-nested-menu';
import { MenuItem } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import React from 'react';
import axios from 'axios';

/**
 * Translates Okta claims configured as SailPoint IdentityNow access
 * profiles to ELM roles (troles.)
 *
 * @param {string} role - Okta role title
 * @returns {string} - ELM role title
 */
const translateOktaRole = (role) => {
  switch (role) {
    case 'ELM Administrator':
      return 'Admin';
    case 'ELM Equipment Manager':
      return 'Equipment Manager';
    case 'ELM Engineering Services':
      return 'ES';
    case 'ELM Material Manager':
      return 'Material Manager';
    case 'ELM Class Store':
      return 'Material Storage';
    case 'ELM Planner':
      return 'Planner';
    case 'ELM Users':
    default:
      return 'Unknown';
  }
};

export default function App(props) {
  const [user, setUser] = useState({});
  const [locationFilter, setLocationFilter] = useState(() => {
    // fetch from localStorage only once at render
    return bake({ key: 'locationFilter' }) === 'enabled';
  });
  const [alertClick, setAlertClick] = useState(false);
  const [devMode, setDevMode] = useState(undefined);
  const [key, setKey] = useState(LINKS.HOME);
  const [isKiosk, setIsKiosk] = useState(props.isKiosk);
  const [roles, setRoles] = useState([]);
  const { authState, oktaAuth } = useOktaAuth();

  /**
   * Add a request interceptor to attach the Okta access token to the authorization header for all requests.
   */
  axios.interceptors.request.use(
    async (config) => {
      const { accessToken } = await oktaAuth.tokenManager.getTokens();
      if (accessToken && !config.data?.noBearerAuth) {
        config.headers.Authorization = `Bearer ${accessToken?.accessToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // memoize getRoles to provide a stable reference for login's useCallback deps
  const getRoles = useCallback(() => {
    Api({
      sp: 'getRolesByUser',
      json: {},
    }).then((response) => {
      bake({ key: 'roles', value: response });
      setRoles(response);
    });
  }, []);

  // memoize login to provide a stable reference for authenticateUser's useCallback deps
  const login = useCallback(() => {
    checkVersion();
    getRoles();
    setEnvironmentVariables();
    // roleUpdate();
  }, [getRoles]);

  // memoize authenticateUser to provide a stable reference for afterClearCache's useCallback deps
  const authenticateUser = useCallback(() => {
    // Kiosk setup is defined by "<elm home url 'elmxxxx.solidapps.net'>/kiosk"
    // Kiosk mode and Playwright test mode are independent conditions.
    if (props.isKiosk) {
      // if the url is kiosk set user info to kiosk
      // when badge is scanned while performing any operation, user info will be sent to the api directly
      localStorage.clear();
      let user = { name: 'kiosk', unique_name: 'kiosk', oid: 'kiosk' };
      setUser(user);
      bake({ key: 'user', value: user });
      bake({ key: 'kioskUserOID', value: user.oid });
      setEnvironmentVariables();
    } else {
      if (!authState || !authState.isAuthenticated) {
        // When user isn't authenticated, forget any user info
        console.log('User is not logged in. Logging in.');
        setUser(null);
        // Auth redirect is owned by RequiredAuth (secureRoute.js).
        // App only renders when authenticated; this branch handles brief
        // transitional states (e.g. token refresh) without forcing a redirect.
      } else {
        console.log('User is logged in.');

        oktaAuth
          .getUser()
          .then((info) => {
            const userClaims = Array.isArray(info.ELMGroupClaim)
              ? info.ELMGroupClaim
              : [];

            const roles = userClaims.map((claim) => {
              return translateOktaRole(claim.toString());
            });

            Api({
              sp: 'addUserDetails',
              json: {
                idsid: info.IDSID,
                oid: info.oid,
                name: info.name,
                email: info.preferred_username,
                empid: info.employeeNumber,
                shouldSetRoles: environment === 'production' ? 1 : 0,
                roles: roles,
              },
            }).then((response) => {
              const newOid = response?.[0]?.['@OID'];
              if (!newOid) {
                toast.error(
                  'Unable to retrieve user account details. Please try again or contact support.'
                );
                return;
              }
              info.oid = newOid;
              setUser(info);
              bake({ key: 'user', value: info });
              login();

              Promise.all([
                fetchUserBusinessUnit(info.email),
                fetchUserLastDay(info.email),
              ]).then(([businessUnit, lastDay]) => {
                if (businessUnit || lastDay) {
                  const updatedInfo = {
                    ...info,
                    ...(businessUnit && { businessUnit }),
                    ...(lastDay && { lastDay }),
                  };
                  setUser(updatedInfo);
                  bake({ key: 'user', value: updatedInfo });
                }
              });
            });
          })
          .catch((err) => {
            console.error(err);
          });
      }
    }
  }, [props.isKiosk, authState, oktaAuth, login]);

  useEffect(() => {
    authenticateUser();
  }, [authenticateUser]);

  const updateLocationFilter = () => {
    setLocationFilter(!locationFilter);
    if (!locationFilter) {
      bake({ key: 'locationFilter', value: 'enabled' });
    } else {
      bake({ key: 'locationFilter', value: 'disabled' });
    }
  };

  useEffect(() => {
    init();
  }, []);

  // memoize init function to prevent unnecessary re-renders
  const init = useCallback(() => {
    window.addEventListener('resize', handleResize);

    setIsKiosk(props.isKiosk);
    bake({ key: 'isKiosk', value: props.isKiosk });
  }, [props.isKiosk, handleResize]);

  // memoize afterClearCache to stabilize the outlet context and avoid unnecessary child re-renders
  const afterClearCache = useCallback(() => {
    setIsKiosk(props.isKiosk);
    bake({ key: 'isKiosk', value: props.isKiosk });
    setLocationFilter(bake({ key: 'locationFilter' }) === 'enabled');
    authenticateUser();
  }, [props.isKiosk, authenticateUser]);

  const viewUpdates = () => {
    setAlertClick(true);
    setKey(LINKS.RELEASENOTES);
  };

  const toggleDevMode = () => {
    let dev =
      !bake({ key: 'devMode' }) || bake({ key: 'devMode' }) === 'false'
        ? 'true'
        : 'false';
    setDevMode(dev === 'true' ? true : undefined);
    bake({ key: 'devMode', value: dev });
  };

  const handleItemClick = (e, loc) => {
    setKey(loc);
    if (e) {
      let element = document.getElementsByClassName('MuiPopover-paper')[0];
      element.classList.add('hide');
    }
  };

  const hasRole = (list) => {
    let match = false;
    list.forEach((l) => {
      roles?.forEach((r) => {
        if (r.Role === l) {
          match = true;
        }
      });
    });
    return match;
  };

  return (
    <div className="main-div">
      <Navbar
        collapseOnSelect
        expand="lg"
        bg="dark"
        variant="dark"
        sticky="top"
        className={
          window.location.href.indexOf('elmo.') !== -1 ||
          window.location.href.indexOf('elm.') !== -1
            ? 'intel-solidigm-gradient'
            : window.location.href.indexOf('elmo-qa') !== -1 ||
                window.location.href.indexOf('elm-qa') !== -1
              ? 'intel-solidigm-gradient-qa'
              : 'intel-solidigm-gradient-dev'
        }
      >
        <Container>
          <LinkContainer to="/home" onClick={() => setKey(LINKS.HOME)}>
            <Navbar.Brand>
              <img
                width="20px"
                height="auto"
                className="img-responsive logo"
                src={Logo}
                alt="logo"
              />
            </Navbar.Brand>
          </LinkContainer>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="me-auto">
              {!isKiosk ? (
                <>
                  {/* Equipment Section*/}

                  <NavDropdown
                    title="Equipment"
                    active={key === LINKS.EQUIPMENT ? true : false}
                  >
                    <NavDropdown.Item
                      as={NavLink}
                      to="/equipment"
                      state={{
                        locationFilter: locationFilter,
                      }}
                      onClick={() => setKey(LINKS.EQUIPMENT)}
                    >
                      Equipment
                    </NavDropdown.Item>

                    {hasRole(['Admin']) ? (
                      <NavDropdown.Item
                        as={NavLink}
                        to="/importPO"
                        onClick={() => setKey(LINKS.EQUIPMENT)}
                      >
                        Import PO/IO
                      </NavDropdown.Item>
                    ) : null}
                  </NavDropdown>

                  {/* Location Section */}
                  <NavDropdown
                    title="Locations"
                    active={
                      key === LINKS.LOCATION || key === LINKS.CORPSERVICE
                        ? true
                        : false
                    }
                  >
                    {' '}
                    <NestedMenuItem
                      className={
                        key === LINKS.CORPSERVICE
                          ? 'activeRoute nested-menu-item'
                          : 'nested-menu-item'
                      }
                      label={'Corporate Services'}
                      parentMenuOpen={true}
                    >
                      <MenuItem
                        className="dropdown-item"
                        as={NavLink}
                        to="/geo"
                        onClick={(e) => handleItemClick(e, LINKS.CORPSERVICE)}
                      >
                        Geos
                      </MenuItem>
                      <MenuItem
                        className="dropdown-item"
                        as={NavLink}
                        to="/site"
                        onClick={(e) => handleItemClick(e, LINKS.CORPSERVICE)}
                      >
                        Sites
                      </MenuItem>
                      <MenuItem
                        className="dropdown-item"
                        as={NavLink}
                        to="/building"
                        onClick={(e) => handleItemClick(e, LINKS.CORPSERVICE)}
                      >
                        Buildings
                      </MenuItem>
                      <MenuItem
                        className="dropdown-item"
                        as={NavLink}
                        to="/floor"
                        onClick={(e) => handleItemClick(e, LINKS.CORPSERVICE)}
                      >
                        Floors
                      </MenuItem>
                    </NestedMenuItem>
                    <NavDropdown.Item
                      as={NavLink}
                      to="/lab"
                      onClick={() => setKey(LINKS.LOCATION)}
                    >
                      Labs
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={NavLink}
                      to="/location"
                      onClick={() => setKey(LINKS.LOCATION)}
                    >
                      Locations
                    </NavDropdown.Item>
                  </NavDropdown>

                  {/* Material Section */}
                  <NavDropdown
                    title="Material"
                    active={key === LINKS.MATERIAL ? true : false}
                  >
                    <NavDropdown.Item
                      as={NavLink}
                      to="/ssd"
                      onClick={() => setKey(LINKS.MATERIAL)}
                    >
                      SSDs
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={NavLink}
                      to="/component"
                      onClick={() => setKey(LINKS.MATERIAL)}
                    >
                      Components
                    </NavDropdown.Item>

                    {/* <NavDropdown.Item
                      as={NavLink}
                      to="/epo"
                      onClick={() => setKey(LINKS.MATERIAL)}
                    >
                      EPO
                    </NavDropdown.Item>

                    <NavDropdown.Item
                      as={NavLink}
                      to="/request"
                      onClick={() => setKey(LINKS.MATERIAL)}
                    >
                      EPO Requests
                    </NavDropdown.Item>

                    <NavDropdown.Item
                      as={NavLink}
                      to="/priority"
                      onClick={() => setKey(LINKS.MATERIAL)}
                    >
                      EPO Priorities
                    </NavDropdown.Item>

                    <NavDropdown.Item
                      as={NavLink}
                      to="/flow"
                      onClick={() => setKey(LINKS.MATERIAL)}
                    >
                      Flow
                    </NavDropdown.Item> */}
                  </NavDropdown>

                  {/* Operations Section */}
                  {/* <NavDropdown
                    title="Operations"
                    active={key === LINKS.OPERATIONS ? true : false}
                  >
                    <NavDropdown.Item
                      as={NavLink}
                      to="/events"
                      onClick={() => setKey(LINKS.OPERATIONS)}
                    >
                      Events
                    </NavDropdown.Item>
                  </NavDropdown> */}

                  {/* Reports Section */}
                  <NavDropdown
                    title="Reports"
                    active={key === LINKS.REPORTS ? true : false}
                  >
                    <NavDropdown.Item
                      as={NavLink}
                      to="/tabularReports"
                      onClick={() => setKey(LINKS.REPORTS)}
                    >
                      Tabular Reports
                    </NavDropdown.Item>
                    {hasRole([
                      'Admin',
                      'Material Manager',
                      'Material Storage',
                    ]) ? (
                      <NavDropdown.Item
                        as={NavLink}
                        to="/geekInventoryReport"
                        onClick={() => setKey(LINKS.REPORTS)}
                      >
                        Geek+ Inventory Report
                      </NavDropdown.Item>
                    ) : null}

                    <NavDropdown.Item
                      as={NavLink}
                      to="/capitalPurchaseReport"
                      onClick={() => setKey(LINKS.REPORTS)}
                    >
                      Capital Purchase Report
                    </NavDropdown.Item>
                  </NavDropdown>

                  {hasRole(['Dev', 'Admin']) ? (
                    <NavDropdown
                      title="Workflows"
                      active={key === LINKS.WORKFLOW ? true : false}
                    >
                      <NavDropdown.Item
                        as={NavLink}
                        to="/statusWorkflow"
                        onClick={() => setKey(LINKS.WORKFLOW)}
                      >
                        Status
                      </NavDropdown.Item>
                    </NavDropdown>
                  ) : null}

                  {/* Help Section */}
                  <NavDropdown title="Help" active={false}>
                    <NavDropdown.Item
                      href="https://npsg-jira.elements.local/secure/CreateIssueDetails!init.jspa?pid=26170&issuetype=11800&summary=Please%20summarize%20your%20feedback%20request%20or%20bug%20report&description=Please%20describe%20your%20feedback%20or%20bug%20report%20in%20more%20detail&labels=elm&labels=feedback&priority=8"
                      target="_blank"
                      active={false}
                      onClick={() => setKey(LINKS.HOME)}
                    >
                      Submit feedback
                    </NavDropdown.Item>

                    <NavDropdown.Item
                      href="https://nandps.sharepoint.com/teams/ELM"
                      target="_blank"
                      active={false}
                      onClick={() => setKey(LINKS.HOME)}
                    >
                      Training
                    </NavDropdown.Item>

                    <NavDropdown.Item
                      as={NavLink}
                      to="/releaseNotes"
                      onClick={() => setKey(LINKS.RELEASENOTES)}
                    >
                      Release Notes
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={NavLink}
                      to="/apiDocs"
                      onClick={() => setKey(LINKS.HOME)}
                    >
                      API Documentation
                    </NavDropdown.Item>
                  </NavDropdown>

                  {/* Admin Section */}
                  {hasRole([
                    'Dev',
                    'Admin',
                    'Equipment Manager',
                    'Material Manager',
                    'Planner',
                    'Material Storage',
                  ]) ? (
                    <NavDropdown
                      title="Admin"
                      active={key === LINKS.ADMIN ? true : false}
                    >
                      <NavDropdown.Item
                        as={NavLink}
                        to="/fields"
                        onClick={() => setKey(LINKS.ADMIN)}
                        roles={roles}
                      >
                        Fields
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={NavLink}
                        to="/groups"
                        roles={roles}
                        onClick={() => setKey(LINKS.ADMIN)}
                      >
                        Groups
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={NavLink}
                        to="/templates"
                        roles={roles}
                        onClick={() => setKey(LINKS.ADMIN)}
                      >
                        Templates
                      </NavDropdown.Item>

                      {hasRole(['Admin']) ? (
                        <NavDropdown.Item
                          as={NavLink}
                          to="/default"
                          onClick={() => setKey(LINKS.ADMIN)}
                        >
                          Default Fields
                        </NavDropdown.Item>
                      ) : null}

                      <NavDropdown.Item
                        as={NavLink}
                        to="/hierarchy"
                        onClick={() => setKey(LINKS.ADMIN)}
                      >
                        Hierarchy
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={NavLink}
                        to="/admin"
                        onClick={() => setKey(LINKS.HOME)}
                      >
                        User Roles
                      </NavDropdown.Item>
                      {hasRole(['Admin']) ? (
                        <NavDropdown.Item
                          as={NavLink}
                          to="/logins"
                          onClick={() => setKey(LINKS.HOME)}
                        >
                          Logins
                        </NavDropdown.Item>
                      ) : null}
                      {hasRole(['Dev', 'Admin']) ? (
                        <NavDropdown.Item
                          as={NavLink}
                          roles={roles}
                          to="/apiManagement"
                          onClick={() => setKey(LINKS.ADMIN)}
                        >
                          API Management
                        </NavDropdown.Item>
                      ) : null}
                    </NavDropdown>
                  ) : null}
                </>
              ) : (
                <Nav.Item>
                  <Nav.Link
                    as={NavLink}
                    to="/kiosk"
                    onClick={() => setKey(LINKS.KIOSK)}
                  >
                    Kiosk
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            {/* User Preferences Section */}
            <Nav>
              {hasRole(['Dev']) ? (
                <Nav.Link as={NavLink} onClick={() => viewUpdates()}>
                  <Hint
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    title="Toggle Dev Mode"
                  >
                    <Magic
                      className={devMode ? 'dev-active' : ''}
                      size={devMode ? 20 : 16}
                      onClick={() => {
                        toggleDevMode();
                      }}
                    ></Magic>
                  </Hint>
                </Nav.Link>
              ) : null}
              {bake({ key: 'userVersion' }) !== bake({ key: 'version' }) &&
              !alertClick ? (
                <Nav.Link
                  as={NavLink}
                  to="/releaseNotes"
                  onClick={() => viewUpdates()}
                >
                  <Hint
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    title="View Release Notes"
                  >
                    <BellFill></BellFill>
                  </Hint>
                </Nav.Link>
              ) : null}
              <Nav.Link onClick={() => updateLocationFilter()}>
                <Hint
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  title={
                    locationFilter
                      ? 'Disable Location Filter'
                      : 'Enabled Location Filter'
                  }
                >
                  {locationFilter ? <HouseFill></HouseFill> : <Globe></Globe>}
                </Hint>
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/userPreferences"
                onClick={() => setKey(LINKS.HOME)}
              >
                {user.name}{' '}
                {window.location.href.indexOf('elm.') !== -1 ? (
                  ''
                ) : window.location.href.indexOf('elm-qa') !== -1 ? (
                  <span>(QA)</span>
                ) : (
                  <span>(DEV)</span>
                )}
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Sitewide Banner */}
      <div className="sitewide-banner">
        To avoid issues with the latest release, please make sure to click the
        Clear Cache button within user preferences (this can be accessed by
        clicking on your name in the top right of ELM).
      </div>

      <Container>
        <ToastContainer autoClose={2000} />
        <Outlet
          // handle toggle click with key to force remount of advancedSearch and reset state
          key={locationFilter?.toString()}
          context={useMemo(
            () => ({
              locationFilter: locationFilter,
              roles: roles,
              init: init,
              afterClearCache: afterClearCache,
            }),
            [locationFilter, roles, init, afterClearCache]
          )}
        />
      </Container>
    </div>
  );
}
